import { useState, type FormEvent } from "react";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/daij6ljoh653ivfvoe5g";

type ParsedResult = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

export default function SubscribeForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const payload = new FormData(formEl);
    const honeypot = String(payload.get("website_alt") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }
    payload.delete("website_alt");

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();

      let parsed: ParsedResult | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResult;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMessage === "string" && serverMessage.trim()
            ? serverMessage
            : "提交失败，请稍后再试",
        );
      }
    } catch (error) {
      setStatus("error");
      setFormError(error instanceof Error ? error.message : "网络异常，请稍后再试");
    }
  };

  return (
    <section id="subscribe" className="w-full bg-background-50">
      <div className="mx-auto max-w-7xl px-4 pb-14 pt-0 md:px-6 md:pb-20">
        <div className="grid grid-cols-1 gap-8 rounded-lg border border-accent-200 bg-accent-100 p-6 md:p-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1 text-xs font-medium text-primary-600">
              <i className="ri-notification-3-line text-sm"></i>
              更新提醒
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              新出的更新，第一时间推给你
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-700">
              不用一直盯着。留下邮箱，今天刚出、正在热传的内容一有补充就提醒你，随时回看刚刚刷屏的那几段。
            </p>
          </div>

          <form
            id="subscribe-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="relative rounded-lg border border-accent-200 bg-background-50 p-5 md:p-6"
          >
            <div className="flex flex-col gap-4">
              <div>
                <label
                  htmlFor="subscribe-email"
                  className="text-xs font-medium text-foreground-700"
                >
                  邮箱地址
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  required
                  placeholder="you@example.com"
                  className="mt-1.5 h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <div>
                <label
                  htmlFor="subscribe-focus"
                  className="text-xs font-medium text-foreground-700"
                >
                  想收到哪一类
                </label>
                <select
                  id="subscribe-focus"
                  name="focus"
                  defaultValue="全部更新"
                  className="mt-1.5 h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400"
                >
                  <option>全部更新</option>
                  <option>今天刚出</option>
                  <option>热传中</option>
                  <option>刚补档</option>
                </select>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="subscribe-form__trap-field"
              />

              <button
                type="submit"
                disabled={status === "loading"}
                className="flex h-11 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <i className="ri-mail-send-line"></i>
                {status === "loading" ? "提交中…" : "订阅更新提醒"}
              </button>

              {status === "success" && (
                <p className="flex items-center gap-1.5 text-xs text-primary-600">
                  <i className="ri-checkbox-circle-line text-sm"></i>
                  订阅成功，有新的热传更新会第一时间提醒你。
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-1.5 text-xs text-primary-700">
                  <i className="ri-error-warning-line text-sm"></i>
                  {formError}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}